import React, { useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import { contactsAPI } from '../../services/api';
import EditInteractionDialog from './EditInteractionDialog';

const getInteractionId = (interaction) => interaction?._id || interaction?.id;

const formatTouchDate = (date) => {
    const touchDate = new Date(date);
    if (Number.isNaN(touchDate.getTime())) {
        return { label: 'Unknown date', time: '' };
    }

    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
    const dateString = touchDate.toDateString();

    return {
        label: today === dateString
            ? 'Today'
            : yesterday === dateString
                ? 'Yesterday'
                : touchDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
        time: touchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
};

const InteractionsList = ({
    interactions: interactionsProp,
    contactId,
    contact,
    onInteractionChange,
    onAddInteractionClick,
    onContactUpdate,
    onSnackbar,
    t = {}
}) => {
    const theme = useTheme();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentInteraction, setCurrentInteraction] = useState(null);
    const resolvedContactId = contactId || contact?._id;
    const touches = [...(interactionsProp || contact?.interactions || [])]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .map((interaction) => ({
            ...interaction,
            touchDate: formatTouchDate(interaction.date)
        }));

    const showSnackbar = (message, severity = 'success') => {
        if (onSnackbar) {
            onSnackbar({ open: true, message, severity });
        }
    };

    const handleUpdatedContact = (updatedContact) => {
        if (updatedContact && onContactUpdate) {
            onContactUpdate(updatedContact);
        }
        if (onInteractionChange) {
            onInteractionChange();
        }
    };

    const handleDeleteInteraction = async (interactionId) => {
        if (!resolvedContactId || !interactionId) return;
        if (window.confirm(t.confirmDeleteTouch || 'Delete this touch?')) {
            try {
                const response = await contactsAPI.deleteInteraction(resolvedContactId, interactionId);
                handleUpdatedContact(response.data);
                showSnackbar(t.touchDeleted || t.interactionDeleted || 'Touch deleted', 'success');
            } catch (error) {
                console.error('Error deleting touch:', error);
                showSnackbar(t.errorDeletingInteraction || 'Error deleting touch', 'error');
            }
        }
    };

    const handleEditClick = (interaction) => {
        setCurrentInteraction(interaction);
        setEditDialogOpen(true);
    };

    const handleEditSave = async (editedInteraction) => {
        if (!resolvedContactId || !currentInteraction) return;
        try {
            const response = await contactsAPI.updateInteraction(
                resolvedContactId,
                getInteractionId(currentInteraction),
                editedInteraction
            );
            handleUpdatedContact(response.data);
            setEditDialogOpen(false);
        } catch (error) {
            console.error('Error updating touch:', error);
            showSnackbar(t.errorUpdatingInteraction || 'Error updating touch', 'error');
        }
    };

    const renderHeader = () => (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            justifyContent="space-between"
            sx={{ mb: 2.5 }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <HistoryIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 650, letterSpacing: 0 }}>
                        {t.touches || t.interactions || 'Touches'}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560, lineHeight: 1.55 }}>
                    {t.touchesSubtitle || 'A quiet history of calls, messages, meetings, and moments worth remembering.'}
                </Typography>
            </Box>

            {onAddInteractionClick && (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAddInteractionClick}
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, whiteSpace: 'nowrap' }}
                >
                    {t.addTouch || t.addInteraction || 'Add touch'}
                </Button>
            )}
        </Stack>
    );

    return (
        <>
            {renderHeader()}

            {!touches.length ? (
                <Box
                    sx={{
                        border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        p: 2.5
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontSize: 15, fontWeight: 650, letterSpacing: 0 }}>
                        {t.noTouches || 'No touches yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
                        {t.noTouchesSubtitle || 'Add a short note after a call, message, meeting, or thoughtful moment.'}
                    </Typography>
                </Box>
            ) : (
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        {touches.length} {touches.length === 1 ? 'touch' : 'touches'}
                        {touches[0]?.touchDate?.label ? ` · Last ${touches[0].touchDate.label.toLowerCase()}` : ''}
                    </Typography>

                    <Stack spacing={1.5}>
                        {touches.map((interaction) => (
                            <Box
                                key={getInteractionId(interaction)}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '18px minmax(0, 1fr)',
                                    gap: 1.5
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.primary.main,
                                            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`
                                        }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                        borderRadius: '8px',
                                        bgcolor: alpha(theme.palette.background.paper, 0.88),
                                        p: 1.75,
                                        transition: 'border-color 0.15s ease, background-color 0.15s ease',
                                        '&:hover': {
                                            borderColor: alpha(theme.palette.primary.main, 0.45),
                                            bgcolor: theme.palette.background.paper,
                                            '& .touch-actions': { opacity: 1 }
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 650, letterSpacing: 0 }}>
                                                {interaction.touchDate.label}
                                            </Typography>
                                            {interaction.touchDate.time && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {interaction.touchDate.time}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Stack
                                            className="touch-actions"
                                            direction="row"
                                            spacing={0.5}
                                            sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.15s ease' }}
                                        >
                                            <Tooltip title={t.editTouch || 'Edit touch'}>
                                                <IconButton
                                                    size="small"
                                                    aria-label="edit touch"
                                                    onClick={() => handleEditClick(interaction)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t.deleteTouch || 'Delete touch'}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    aria-label="delete touch"
                                                    onClick={() => handleDeleteInteraction(getInteractionId(interaction))}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1,
                                            color: interaction.notes ? 'text.primary' : 'text.secondary',
                                            fontStyle: interaction.notes ? 'normal' : 'italic',
                                            lineHeight: 1.6,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        {interaction.notes || t.noTouchNotes || 'No note added'}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            <EditInteractionDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                interaction={currentInteraction}
                onSave={handleEditSave}
            />
        </>
    );
};

export default InteractionsList;
